import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { createMachine, updateMachine, getMachineBySlug } from '../services/apiService';
import ImageUploader from '../components/ImageUploader';
import GalleryUploader from '../components/GalleryUploader';

const MachineForm = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const isEdit = !!slug;

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    power: '',
    workArea: '',
    tableHeight: '',
    materials: '',
    price: 'Sur devis',
    image: '',
    gallery: [],
    description: '',
    longDescription: '',
    features: '',
    type: 'co2',
    youtubeVideo: '',
    cooling: '',
    voltage: '220 V - 50/60 Hz',
    includedEquipment: '',
    availableOptions: '',
    applications: '',
    warrantyMachine: '2 ans (retour atelier)',
    warrantyParts: '12 mois',
    models: '' // Format: "Modèle 1|100W|1000x600mm\nModèle 2|130W|1300x900mm"
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Matériaux disponibles
  const availableMaterials = [
    { name: 'Bois & dérivés (bambou, MDF, etc.)', key: 'wood' },
    { name: 'Acrylique', key: 'acrylic' },
    { name: 'Verre', key: 'glass' },
    { name: 'Papier', key: 'paper' },
    { name: 'Tissu', key: 'fabric' },
    { name: 'Cuir', key: 'leather' },
    { name: 'Caoutchouc', key: 'rubber' },
    { name: 'Métal peint', key: 'painted_metal' },
    { name: 'Métal', key: 'metal' },
    { name: 'Pierre & Marbre', key: 'stone_marble' },
    { name: 'Céramique', key: 'ceramic' }
  ];

  const metalTypes = [
    'Acier doux', 'Acier inoxydable', 'Aluminium',
    'Cuivre', 'Laiton', 'Titane', 'Acier galvanisé',
    'Or', 'Argent'
  ];

  const [materialsCompatibility, setMaterialsCompatibility] = useState([]);
  const [metalCuttingTable, setMetalCuttingTable] = useState([]);
  const [showMetalCutting, setShowMetalCutting] = useState(false);


  useEffect(() => {
    if (isEdit && slug) {
      fetchMachine();
    }
  }, [isEdit, slug]);

  const fetchMachine = async () => {
    try {
      const machine = await getMachineBySlug(slug);
      setFormData({
        ...machine,
        materials: machine.materials.join(', '),
        gallery: Array.isArray(machine.gallery) ? machine.gallery : [],
        features: machine.features.join('\n'),
        includedEquipment: machine.includedEquipment?.join('\n') || '',
        availableOptions: machine.availableOptions?.join('\n') || '',
        applications: machine.applications?.join('\n') || '',
        models: machine.models || '',
        cooling: machine.technicalSpecs?.cooling || '',
        voltage: machine.technicalSpecs?.voltage || '220 V - 50/60 Hz',
        warrantyMachine: machine.warranty?.machine || '2 ans (retour atelier)',
        warrantyParts: machine.warranty?.parts || '12 mois'
      });
      
      // Charger les compatibilités matériaux
      if (machine.materialCompatibility && machine.materialCompatibility.length > 0) {
        setMaterialsCompatibility(machine.materialCompatibility);
      }
      
      // Charger le tableau de découpe métaux s'il existe
      if (machine.metalCuttingSpecs && machine.metalCuttingSpecs.length > 0) {
        setMetalCuttingTable(machine.metalCuttingSpecs);
        setShowMetalCutting(true);
      }
    } catch (error) {
      console.error('Error fetching machine:', error);
      setError('Erreur lors du chargement de la machine');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Gérer les changements de compatibilité matériaux
  const handleMaterialToggle = (materialKey, capability) => {
    setMaterialsCompatibility(prev => {
      const existingIndex = prev.findIndex(m => m.material === materialKey);
      
      if (existingIndex >= 0) {
        // Matériau existe, mettre à jour la capacité
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          [capability]: !updated[existingIndex][capability]
        };
        
        // Retirer si aucune capacité n'est cochée
        if (!updated[existingIndex].engrave && !updated[existingIndex].cut) {
          return prev.filter((_, i) => i !== existingIndex);
        }
        
        return updated;
      } else {
        // Nouveau matériau
        return [...prev, {
          material: materialKey,
          engrave: capability === 'engrave',
          cut: capability === 'cut'
        }];
      }
    });
  };

  // Ajouter une ligne au tableau de découpe métaux
  const addMetalCuttingRow = () => {
    setMetalCuttingTable(prev => [...prev, { metalType: '', power: '', minThickness: '', maxThickness: '' }]);
  };

  // Retirer une ligne du tableau de découpe métaux
  const removeMetalCuttingRow = (index) => {
    setMetalCuttingTable(prev => prev.filter((_, i) => i !== index));
  };

  // Mettre à jour une ligne du tableau de découpe métaux
  const updateMetalCuttingRow = (index, field, value) => {
    setMetalCuttingTable(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Vérifier si un matériau/capacité est coché
  const isMaterialChecked = (materialKey, capability) => {
    const material = materialsCompatibility.find(m => m.material === materialKey);
    return material ? material[capability] : false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Transform data for API
      const machineData = {
        name: formData.name,
        slug: formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-'),
        power: formData.power,
        workArea: formData.workArea,
        tableHeight: formData.tableHeight,
        materials: formData.materials.split(',').map(m => m.trim()),
        price: formData.price,
        image: formData.image,
        gallery: Array.isArray(formData.gallery) ? formData.gallery : [],
        description: formData.description,
        longDescription: formData.longDescription,
        features: formData.features.split('\n').filter(f => f.trim()),
        type: formData.type,
        models: formData.models || null,
        youtubeVideo: formData.youtubeVideo,
        technicalSpecs: {
          cooling: formData.cooling,
          voltage: formData.voltage,
          tableTypes: [
            { name: "Nid d'abeille", description: "Idéal pour la gravure et la découpe de petites pièces" },
            { name: "Lames", description: "Parfaite pour les matériaux épais et la découpe propre" }
          ]
        },
        includedEquipment: formData.includedEquipment.split('\n').filter(i => i.trim()),
        availableOptions: formData.availableOptions.split('\n').filter(o => o.trim()),
        deliveryOptions: [
          {
            title: "Retrait à l'atelier (Moidieu - 38 ou Lyon - 69)",
            items: [
              "Idéal pour les modèles compacts",
              "Sur place, nous assurons :",
              "• La remise de la machine testée & prête à l'emploi",
              "• L'installation du logiciel sur votre ordinateur",
              "• Une formation de prise en main directement dans nos locaux"
            ]
          },
          {
            title: "Livraison & installation chez vous (sur devis)",
            items: [
              "Recommandé pour les équipements plus volumineux",
              "Nous livrons la machine dans vos locaux et assurons sur place :",
              "• L'installation complète",
              "• La configuration du logiciel sur votre ordinateur",
              "• Une formation pour démarrer sereinement"
            ]
          }
        ],
        warranty: {
          machine: formData.warrantyMachine,
          parts: formData.warrantyParts
        },
        materialCompatibility: materialsCompatibility,
        metalCuttingSpecs: metalCuttingTable.filter(row => row.metalType && row.power && row.minThickness && row.maxThickness),
        applications: formData.applications.split('\n').filter(a => a.trim())
      };

      if (isEdit) {
        await updateMachine(formData.id, machineData);
      } else {
        await createMachine(machineData);
      }

      navigate('/admin/dashboard');
    } catch (err) {
      console.error('Error saving machine:', err);
      setError('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Retour au dashboard
        </button>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <h1 className="text-3xl font-bold mb-8" style={{ color: '#5C3A21' }}>
            {isEdit ? 'Modifier la machine' : 'Ajouter une machine'}
          </h1>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Informations de base */}
            <div className="space-y-4">
              <h2 className="text-xl font-bold" style={{ color: '#5C3A21' }}>Informations de base</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Nom de la machine *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                    placeholder="LaserCO2 Pro 1000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Slug (URL) *</label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                    placeholder="laser-co2-pro-1000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Type *</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  >
                    <option value="co2">CO2</option>
                    <option value="fiber">Fibre</option>
                    <option value="mixed">Mixte</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Puissance minimum</label>
                  <input
                    type="text"
                    name="power"
                    value={formData.power}
                    onChange={handleChange}
                    className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-[#F5EBDD] outline-none"
                    placeholder="40W, 60W, 100W..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Optionnel - Indiquez si plusieurs configurations</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Zone de travail minimum</label>
                  <input
                    type="text"
                    name="workArea"
                    value={formData.workArea}
                    onChange={handleChange}
                    className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-[#F5EBDD] outline-none"
                    placeholder="600 x 400 mm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Optionnel - Dimensions minimales si plusieurs modèles</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Prix</label>
                  <input
                    type="text"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                    placeholder="Sur devis"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Matériaux compatibles (séparés par des virgules) *</label>
                <input
                  type="text"
                  name="materials"
                  value={formData.materials}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  placeholder="Bois, Acrylique, Cuir, Tissu"
                />
                <p className="text-xs text-gray-500 mt-1">Liste simple pour affichage public</p>
              </div>

              {/* Tableau de compatibilité matériaux */}
              <div className="border rounded-lg p-4" style={{ borderColor: '#F5EBDD' }}>
                <h3 className="font-semibold mb-3" style={{ color: '#5C3A21' }}>
                  Compatibilité détaillée des matériaux (optionnel)
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Cochez les capacités (Gravure/Découpe) pour chaque matériau compatible
                </p>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b" style={{ borderColor: '#F5EBDD' }}>
                        <th className="text-left py-2 px-2 font-semibold">Matériau</th>
                        <th className="text-center py-2 px-2 font-semibold">Gravure</th>
                        <th className="text-center py-2 px-2 font-semibold">Découpe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {availableMaterials.map((material) => (
                        <tr key={material.key} className="border-b border-gray-100">
                          <td className="py-2 px-2">{material.name}</td>
                          <td className="text-center py-2 px-2">
                            <input
                              type="checkbox"
                              checked={isMaterialChecked(material.key, 'engrave')}
                              onChange={() => handleMaterialToggle(material.key, 'engrave')}
                              className="w-4 h-4 rounded focus:ring-2"
                              style={{ accentColor: '#5C3A21' }}
                            />
                          </td>
                          <td className="text-center py-2 px-2">
                            <input
                              type="checkbox"
                              checked={isMaterialChecked(material.key, 'cut')}
                              onChange={() => handleMaterialToggle(material.key, 'cut')}
                              className="w-4 h-4 rounded focus:ring-2"
                              style={{ accentColor: '#5C3A21' }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Tableau de découpe métaux (optionnel) */}
              <div className="border rounded-lg p-4" style={{ borderColor: '#F5EBDD' }}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-semibold" style={{ color: '#5C3A21' }}>
                      Spécifications de découpe métaux (optionnel)
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Pour machines fibre - Spécifiez la puissance et épaisseur max par type de métal
                    </p>
                  </div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showMetalCutting}
                      onChange={(e) => setShowMetalCutting(e.target.checked)}
                      className="w-4 h-4 rounded"
                      style={{ accentColor: '#5C3A21' }}
                    />
                    <span className="text-sm">Activer</span>
                  </label>
                </div>

                {showMetalCutting && (
                  <div className="space-y-3">
                    {metalCuttingTable.map((row, index) => (
                      <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end p-3 bg-gray-50 rounded-lg">
                        <div>
                          <label className="block text-xs font-medium mb-1">Type de métal</label>
                          <select
                            value={row.metalType || ''}
                            onChange={(e) => updateMetalCuttingRow(index, 'metalType', e.target.value)}
                            className="w-full px-3 py-2 text-sm rounded border focus:ring-2 focus:ring-amber-400 outline-none"
                          >
                            <option value="">Sélectionner...</option>
                            {metalTypes.map(metal => (
                              <option key={metal} value={metal}>{metal}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Puissance</label>
                          <input
                            type="text"
                            value={row.power || ''}
                            onChange={(e) => updateMetalCuttingRow(index, 'power', e.target.value)}
                            placeholder="ex: 1000W"
                            className="w-full px-3 py-2 text-sm rounded border focus:ring-2 focus:ring-amber-400 outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Épaisseur min</label>
                          <input
                            type="text"
                            value={row.minThickness || ''}
                            onChange={(e) => updateMetalCuttingRow(index, 'minThickness', e.target.value)}
                            placeholder="ex: 0.5mm"
                            className="w-full px-3 py-2 text-sm rounded border focus:ring-2 focus:ring-amber-400 outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Épaisseur max</label>
                          <input
                            type="text"
                            value={row.maxThickness || ''}
                            onChange={(e) => updateMetalCuttingRow(index, 'maxThickness', e.target.value)}
                            placeholder="ex: 8mm"
                            className="w-full px-3 py-2 text-sm rounded border focus:ring-2 focus:ring-amber-400 outline-none"
                          />
                        </div>
                        <div>
                          <button
                            type="button"
                            onClick={() => removeMetalCuttingRow(index)}
                            className="w-full px-3 py-2 text-sm rounded bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    <button
                      type="button"
                      onClick={addMetalCuttingRow}
                      className="w-full px-4 py-2 rounded-lg border-2 border-dashed font-medium hover:bg-gray-50 transition-colors"
                      style={{ borderColor: '#5C3A21', color: '#5C3A21' }}
                    >
                      + Ajouter un type de métal
                    </button>
                  </div>
                )}
              </div>

              <div>
                <ImageUploader
                  label="Image principale"
                  required
                  value={formData.image}
                  onChange={(v) => setFormData((prev) => ({ ...prev, image: v }))}
                  hint="Image principale qui apparaîtra sur la liste des machines"
                />
              </div>

              <div>
                <GalleryUploader
                  label="Galerie d'images"
                  value={formData.gallery}
                  onChange={(arr) => setFormData((prev) => ({ ...prev, gallery: arr }))}
                  maxImages={12}
                />
                <p className="text-xs text-gray-600 mt-1">Ajoutez plusieurs photos qui seront affichées sur la page de la machine</p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Modèles disponibles (optionnel)</label>
                <textarea
                  name="models"
                  value={formData.models}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  placeholder="LT 350|80W|900 x 600 mm&#10;LT 460|100W|1300 x 900 mm&#10;LT 600|130W|1600 x 1000 mm"
                />
                <p className="text-xs text-gray-600 mt-1">Format : Nom du modèle|Puissance|Dimensions (une ligne par modèle)</p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Description courte *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows="2"
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Description longue</label>
                <textarea
                  name="longDescription"
                  value={formData.longDescription}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Points forts (un par ligne) *</label>
                <textarea
                  name="features"
                  value={formData.features}
                  onChange={handleChange}
                  required
                  rows="4"
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  placeholder="Gravure haute précision&#10;Découpe rapide&#10;Logiciel intuitif inclus"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Équipements inclus (un par ligne)</label>
                <textarea
                  name="includedEquipment"
                  value={formData.includedEquipment}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Options disponibles (une par ligne)</label>
                <textarea
                  name="availableOptions"
                  value={formData.availableOptions}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Applications (une par ligne)</label>
                <textarea
                  name="applications"
                  value={formData.applications}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">ID Vidéo YouTube (optionnel)</label>
                <input
                  type="text"
                  name="youtubeVideo"
                  value={formData.youtubeVideo}
                  onChange={handleChange}
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
                  placeholder="dQw4w9WgXcQ"
                />
              </div>
            </div>

            <div className="flex items-center justify-end space-x-4 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate('/admin/dashboard')}
                className="px-6 py-3 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 rounded-lg text-white font-semibold flex items-center space-x-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                style={{ backgroundColor: '#5C3A21' }}
              >
                <Save className="w-5 h-5" />
                <span>{loading ? 'Enregistrement...' : 'Enregistrer'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MachineForm;
